import React, { FC, useState } from 'react';

import { ChevronRight } from '@gravity-ui/icons';
import { Button, Icon } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { Collapsible } from 'client/blocks/Collapsible/Collapsible';
import { getMarketDeviceId, MarketDevice } from 'client/model/devices';
import { SmartHome } from 'client/model/skill';
import * as keyset from 'i18n/Settings';

import styles from './DeviceLinking.module.scss';
import { DeviceLinkingCard } from './DeviceLinkingCard';

const t = i18n(keyset);

interface DeviceLinkingAlreadyConnectedListProps {
    aliceApp: SmartHome;
    isDraft: boolean;
    devices: MarketDevice[];
    afterDeviceRemove: (deviceId: string) => void;
    disabled?: boolean;
}

interface DeviceSectionProps {
    aliceApp: SmartHome;
    isDraft: boolean;
    title: string;
    devices: MarketDevice[];
    afterDeviceRemove: (deviceId: string) => void;
    disabled?: boolean;
    defaultExpanded?: boolean;
}

const DeviceLinkingListStub: FC = () => <div className={styles.deviceList__stub}>{t('devices-empty-list')}</div>;

const DeviceSection: FC<DeviceSectionProps> = ({
    aliceApp,
    isDraft,
    title,
    devices,
    afterDeviceRemove,
    disabled,
    defaultExpanded = true,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <div className={styles.deviceList__section}>
            <Button className={styles.deviceList__expandButton} onClick={() => setExpanded(!expanded)}>
                <Icon
                    data={ChevronRight}
                    size={16}
                    className={`${styles.deviceList__sectionIcon} ${expanded ? styles.deviceList__sectionIcon_expanded : ''}`}
                />
                {title}
            </Button>
            <Collapsible visible={expanded}>
                {devices.map((device) => {
                    const deviceId = getMarketDeviceId(device);
                    const afterRemove = () => afterDeviceRemove(deviceId);

                    return (
                        <div className={styles.deviceList__item} key={deviceId}>
                            <DeviceLinkingCard
                                aliceApp={aliceApp}
                                isDraft={isDraft}
                                device={device}
                                afterRemove={afterRemove}
                                disabled={disabled}
                            />
                        </div>
                    );
                })}
            </Collapsible>
        </div>
    );
};

export const DeviceLinkingAlreadyConnectedList: FC<DeviceLinkingAlreadyConnectedListProps> = ({
    aliceApp,
    isDraft,
    devices,
    afterDeviceRemove,
    disabled,
}) => {
    if (devices.length === 0) {
        return <DeviceLinkingListStub />;
    }

    const [publishedDevices, newDevices] = devices.reduce(
        (acc, cur) => {
            if (cur.isPublished) {
                acc[0].push(cur);
            } else {
                acc[1].push(cur);
            }

            return acc;
        },
        [[], []] as [MarketDevice[], MarketDevice[]],
    );

    const hasNewDevices = newDevices.length > 0;
    const hasPublishedDevices = publishedDevices.length > 0;

    const newDevicesTitle = hasPublishedDevices ? t('devices-new-devices') : t('devices-added-devices');

    return (
        <div>
            {hasNewDevices && (
                <DeviceSection
                    aliceApp={aliceApp}
                    isDraft={isDraft}
                    title={newDevicesTitle}
                    devices={newDevices}
                    afterDeviceRemove={afterDeviceRemove}
                    disabled={disabled}
                    defaultExpanded={true}
                />
            )}
            {hasPublishedDevices && (
                <DeviceSection
                    aliceApp={aliceApp}
                    isDraft={isDraft}
                    title={t('devices-added-earlier')}
                    devices={publishedDevices}
                    afterDeviceRemove={afterDeviceRemove}
                    disabled={disabled}
                    defaultExpanded={true}
                />
            )}
        </div>
    );
};
